import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, User, Users, Briefcase } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  individual: {
    title: 'INDIVIDUALES',
    description: 'Para una persona enfocada en su salud o rendimiento personal',
    icon: User,
    color: 'cyan',
  },
  family: {
    title: 'FAMILIARES',
    description: 'Para gestionar la salud de toda la familia desde un solo lugar',
    icon: Users,
    color: 'purple',
  },
  team: {
    title: 'EQUIPOS',
    description: 'Para entrenadores y atletas trabajando juntos',
    icon: Users,
    color: 'emerald',
  },
  coach: {
    title: 'COACHES',
    description: 'Para profesionales que gestionan múltiples clientes',
    icon: Briefcase,
    color: 'amber',
  },
};

// Hard-coded fallback so /planes always shows the catalog, even if the
// billing API stalls. Mirrors backend PLAN_CATALOG.
const PLANS_FALLBACK = [
  { id: 'free_explore', type: 'individual', name: 'Onor Explorar Gratis', price_mxn: 0,
    features: ['Exploración básica', 'Sin plan personalizado'], max_users: 1 },
  { id: 'salud_individual', type: 'individual', name: 'Onor Salud Individual', price_mxn: 199,
    features: ['Plan personalizado', 'Seguimiento biométrico', 'Notificaciones'], max_users: 1 },
  { id: 'performance_individual', type: 'individual', name: 'Onor Rendimiento Individual', price_mxn: 349,
    features: ['Plan avanzado', 'Métricas de rendimiento', 'Evaluaciones'], max_users: 1 },
  { id: 'salud_familiar', type: 'family', name: 'Onor Salud Familiar', price_mxn: 599,
    features: ['Hasta 4 usuarios', 'Planes personalizados', 'Dashboard familiar'], max_users: 4 },
  { id: 'performance_team', type: 'team', name: 'Onor Rendimiento Equipo', price_mxn: 2999,
    features: ['Hasta 11 atletas', 'Dashboard de equipo', 'Análisis avanzado'], max_users: 11 },
  { id: 'coach_basic', type: 'coach', name: 'Onor Coach Básico', price_mxn: 999,
    features: ['Hasta 20 clientes', 'Alertas básicas', 'Planes para clientes'], max_clients: 20 },
  { id: 'coach_pro', type: 'coach', name: 'Onor Coach Pro', price_mxn: 1999,
    features: ['Hasta 50 clientes', 'Alertas avanzadas', 'Reportes'], max_clients: 50 },
  { id: 'coach_elite', type: 'coach', name: 'Onor Coach Élite', price_mxn: 3499,
    features: ['Hasta 100 clientes', 'Todas las funciones', 'Soporte prioritario'], max_clients: 100 },
];

const Plans = () => {
  // Si hay sesión, esta página sirve para SUBIR de plan, no para registrarse.
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await axios.get(`${API}/billing/plans`);
        if (res.data?.plans?.length) {
          setPlans(res.data.plans);
        } else {
          setPlans(PLANS_FALLBACK);
        }
      } catch (err) {
        console.error('Error cargando planes:', err);
        setPlans(PLANS_FALLBACK); // never leave the page empty
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);

  // Agrupar por tipo, preservando el orden lógico
  const CATEGORY_ORDER = ['individual', 'family', 'team', 'coach'];
  const groupedPlans = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = plans.filter((p) => (p.type || 'individual') === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/app-icon-square.webp" alt="Onor Code" className="h-10 w-10 rounded" />
            <span className="font-teko text-xl text-white tracking-wider">[ONOR] CODE</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              // Con sesión iniciada NO se ofrece registro: se vuelve al panel.
              <Link to="/app/dashboard">
                <Button className="btn-primary" data-testid="plans-back-to-app">IR A MI PANEL</Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:inline-flex">
                  <Button variant="ghost" className="text-zinc-400 hover:text-white">
                    Iniciar sesión
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="btn-primary">REGISTRARSE</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Title */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>
        <h1 className="font-teko text-5xl text-white uppercase tracking-tight">
          NUESTROS <span className="text-gradient-cyber">PLANES</span>
        </h1>
        <p className="text-zinc-400 mt-3 max-w-2xl font-manrope">
          Precios en pesos mexicanos (MXN). Cancela cuando quieras. Sin cargos ocultos.
        </p>
      </section>

      {/* Plan categories */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-16">
          {Object.entries(groupedPlans).map(([type, typePlans]) => {
            const config = CATEGORY_CONFIG[type] || CATEGORY_CONFIG.individual;
            const Icon = config.icon;
            return (
              <section key={type} data-testid={`plan-section-${type}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-10 h-10 rounded-sm bg-${config.color}-500/10 border border-${config.color}-500/30 flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 text-${config.color}-400`} />
                  </div>
                  <div>
                    <h2 className="font-teko text-3xl text-white uppercase">{config.title}</h2>
                    <p className="text-zinc-500 text-sm">{config.description}</p>
                  </div>
                </div>

                <div className={`grid grid-cols-1 ${typePlans.length >= 3 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                  {typePlans.map((plan) => (
                    <div
                      key={plan.id}
                      className="card-default flex flex-col"
                      data-testid={`plan-${plan.id}`}
                    >
                      <h3 className="font-teko text-2xl text-white mb-1">
                        {plan.name.replace(/^Onor /i, '')}
                      </h3>
                      <p className="text-3xl font-bold text-white mb-1">
                        ${plan.price_mxn?.toLocaleString('es-MX')}
                        <span className="text-sm text-zinc-500 font-normal"> MXN/mes</span>
                      </p>
                      {plan.max_users && (
                        <p className="text-zinc-500 text-xs mb-4">
                          Hasta {plan.max_users} usuario{plan.max_users > 1 ? 's' : ''}
                        </p>
                      )}
                      {plan.max_clients && (
                        <p className="text-zinc-500 text-xs mb-4">
                          Hasta {plan.max_clients} clientes
                        </p>
                      )}
                      <ul className="space-y-2 mb-6 flex-1">
                        {plan.features?.map((f) => (
                          <li key={f} className="text-zinc-400 text-sm flex items-start gap-2">
                            <CheckCircle className={`w-4 h-4 text-${config.color}-400 flex-shrink-0 mt-0.5`} />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      {user ? (
                        // Usuario con sesión: sube de plan con SU cuenta (nunca
                        // al registro). Suscripción valida y abre el upgrade.
                        plan.price_mxn === 0 ? (
                          <Button className="btn-secondary w-full" disabled data-testid={`btn-${plan.id}`}>
                            PLAN GRATUITO
                          </Button>
                        ) : (
                          <Link to={`/app/billing?upgrade=${plan.id}`}>
                            <Button className="btn-primary w-full" data-testid={`btn-${plan.id}`}>
                              SUBIR A ESTE PLAN
                            </Button>
                          </Link>
                        )
                      ) : (
                        <Link to={`/register?plan=${plan.id}`}>
                          <Button className="btn-secondary w-full" data-testid={`btn-${plan.id}`}>
                            {plan.price_mxn === 0 ? 'COMENZAR GRATIS' : 'ELEGIR PLAN'}
                          </Button>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* CTA */}
      <section className="border-t border-zinc-800 py-16 px-4 text-center">
        <h2 className="font-teko text-3xl text-white uppercase mb-4">
          ¿No sabes cuál elegir?
        </h2>
        <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
          Comienza con el plan gratuito y prueba la plataforma. Puedes cambiar de plan cuando quieras.
        </p>
        {user ? (
          <Link to="/app/billing">
            <Button className="btn-primary text-lg px-10">
              VER MI SUSCRIPCIÓN
            </Button>
          </Link>
        ) : (
          <Link to="/register">
            <Button className="btn-primary text-lg px-10">
              CREAR CUENTA GRATIS
            </Button>
          </Link>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/brand/app-icon-square.webp" alt="Onor Code" className="h-8 w-8 rounded" />
            <span className="font-teko text-lg text-zinc-400">[ONOR] CODE</span>
          </div>
          <p className="text-zinc-600 text-sm">© 2026 Onor Code. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Plans;
